import * as React from 'react';
import classnames from 'classnames';
import ReactDatePicker from 'react-datepicker';
import { Preconditions } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import { Permission } from 'core/api';
import { DateUtils } from 'core/utils';
import * as UI from 'core/ui';
import { Res, MonthlyAppointmentsDTO, AppointmentInListDTO, AppointmentStatus } from 'core/api';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize, TableCompanionsIcon, ThemeManager } from 'pos/ui';
import * as Images from 'pos/Images';
import { AppointmentFacade } from 'pos/facades';
import { SaleModuleNextAction } from 'pos/modules/helpers/SaleModuleNextAction';
import { Cols, Col, Flex } from 'core/ui';
import { Message, Modal } from 'core/ui-next/modal';

let lastCurrentDate: Date = new Date();
let lastFilterByPendingStatus = true;

const MONDAY = 1;

function getPreviousMondayFrom(date: Date): Date {
	let currentDate = new Date(date);
	currentDate = DateUtils.addDays(date, -1);

	while (currentDate.getDay() !== MONDAY)
		currentDate = DateUtils.addDays(currentDate, -1);

	return currentDate;
}

function getFromDate(currentDate: Date): Date {
	const firstDayOfTheMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
	return getPreviousMondayFrom(firstDayOfTheMonth);
}

function getToDate(currentDate: Date): Date {
	return DateUtils.addDays(getFromDate(currentDate), 41);
}

type Props = { currentDate: Date, monthlyAppointments: MonthlyAppointmentsDTO, filterByPendingStatus: boolean } & Modal.Actions<SaleModuleNextAction>;
type State = {
	currentDate: Date,
	monthlyAppointments: MonthlyAppointmentsDTO,
	filterByPendingStatus: boolean,
	selectedAppointment: Nullable<AppointmentInListDTO>
};

class ShowAppointmentsScreen extends PosScreenClassComponent<Props, State> {

	protected getScreenId(): string {
		return 'SHOW_APPOINTMENTS';
	}

	protected getTitle(): string {
		return Res.Web.SHOW_APPOINTMENTS_TITLE();
	}

	protected getImage(): string | SvgIcon {
		return Images.ShowAppointments;
	}

	protected getSize(): ScreenSize {
		return 'full-screen';
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [
			this.cancelButton(),
			// En el teléfono sólo dejo atender reservas, ni cancelar, ni crear, ni editar, ni nada
			ThemeManager.isPhone()
				? null
				: { title: Res.Web.SHOW_APPOINTMENTS_CANCEL_APPOINTMENT_BTN(), image: Images.Cancel, action: this.cancelSelectedAppointment },
			null,
			null,
			null,
			{ title: Res.Web.SHOW_APPOINTMENTS_CLOSE_APPOINTMENT_BTN(), image: Images.Attend, action: this.closeSelectedAppointment }
		];
	}

	createInitialState({ currentDate, monthlyAppointments, filterByPendingStatus }: Props): Nullable<State> {
		return {
			currentDate,
			monthlyAppointments,
			filterByPendingStatus,
			selectedAppointment: null
		};
	}

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
		lastCurrentDate = this.state.currentDate;
		lastFilterByPendingStatus = this.state.filterByPendingStatus;
	}

	protected changeCurrentDate = async (newCurrentDate: Date) => {
		const from = getFromDate(newCurrentDate);
		const to = getToDate(newCurrentDate);
		const calendar = await AppointmentFacade.getMonthlyAppointments(newCurrentDate, from, to);
		this.setState({ currentDate: newCurrentDate, monthlyAppointments: calendar, selectedAppointment: null });
	};

	protected loadAppointments = async () => {
		const monthlyAppointments = await AppointmentFacade.getMonthlyAppointments(this.state.currentDate, getFromDate(this.state.currentDate), getToDate(this.state.currentDate));
		this.setState({ monthlyAppointments });
	};

	protected createNewAppointment = async () => {
		const { currentDate } = this.state;
		const now = new Date();
		const creationDate = new Date(currentDate);

		creationDate.setHours(now.getHours());
		creationDate.setMinutes(now.getMinutes());
		creationDate.setSeconds(now.getSeconds());

		const appointment = await AppointmentFacade.createAt(creationDate);
		await Command.execute('editAppointment', appointment);
		await this.loadAppointments();
	};

	protected editSelectedAppointment = async () => {
		if (!this.state.selectedAppointment)
			return;

		const appointment = await AppointmentFacade.edit(this.state.selectedAppointment!.Id);
		await Command.execute('editAppointment', appointment);
		await this.loadAppointments();
	};

	protected deleteSelectedAppointment = async () => {
		if (!this.state.selectedAppointment)
			return;


		const result = await Message.confirm(Res.Msg.MSG_CONFIRM_APPOINTMENT_DELETION());
		if (result.ok) {
			await Command.execute('deleteAppointment', this.state.selectedAppointment!.Id);
			await this.loadAppointments();
		}
	};

	protected cancelSelectedAppointment = async () => {
		if (!this.state.selectedAppointment)
			return;

		await Command.execute('cancelAppointment', this.state.selectedAppointment.Id);
		await this.loadAppointments();
	};

	protected closeSelectedAppointment = async () => {
		if (!this.state.selectedAppointment)
			return;

		const appointment = await AppointmentFacade.edit(this.state.selectedAppointment.Id);
		const closeResult = await Command.execute('closeAppointment', appointment);
		if (closeResult.ok) {
			this.close({ type: 'loadTicket', ticketId: closeResult.value.Id });
		}
	};

	protected onFilterByPendingStatusChanged = (value: boolean) => this.setState({ filterByPendingStatus: value, selectedAppointment: null });
	protected selectAppointment = (appointment: AppointmentInListDTO) => this.setState({ selectedAppointment: appointment });

	protected getContent(): React.ReactNode {
		const { Button } = UI;

		const onCurrentDateChanged = async (value: Nullable<Date>) => {
			if (!value)
				return;

			await this.changeCurrentDate(value);
		};

		const filteredAppointments = this.state.filterByPendingStatus
			? this.state.monthlyAppointments.CurrentAppointments.filter(a => a.Status === AppointmentStatus.Pending)
			: this.state.monthlyAppointments.CurrentAppointments;

		const colWidths = {
			time: '50px',
			guests: '50px',
			customer: '*',
			status: '25px',
			location: '120px',
			notes: '*'
		};

		const appointments = filteredAppointments.map((a, idx) => {
			const isSelected = this.state.selectedAppointment && this.state.selectedAppointment.Id === a.Id;
			const className = classnames({
				selected: isSelected,
				'text-16': true,
				'text-muted': a.Status !== AppointmentStatus.Pending,
				'text-line-through': a.Status === AppointmentStatus.Cancelled
			});

			return <li key={idx} className={className} style={{ position: 'relative', minHeight: 60 }} onClick={() => this.selectAppointment(a)}>
				{isSelected ? <Cols className='btn-group hidden-xs' style={{ position: 'absolute', top: 0, right: 0 }}>
					<UI.Button className='btn-sm text-info' onClick={this.editSelectedAppointment}><span className='fa fa-edit'></span> {Res.Common.EDIT_BTN()}</UI.Button>
					<UI.Button className='btn-sm text-danger' onClick={this.deleteSelectedAppointment}><span className='fa fa-minus'></span> {Res.Common.DELETE_BTN()}</UI.Button>
				</Cols> : null}
				<Cols>
					<Col width={colWidths.status} className='hidden-xs'>
						<span className={classnames({
							'fa fa-check text-success': a.Status === AppointmentStatus.Closed,
							'fa fa-ban text-danger': a.Status === AppointmentStatus.Cancelled
						})} />
					</Col>
					<Col width={colWidths.time}>
						{UI.Format.asTime(a.Date)}
					</Col>
					<Col width={colWidths.guests} className='text-center'>
						<TableCompanionsIcon value={a.Guests} />
					</Col>
					<Col width={colWidths.customer}>
						{a.Customer}
						<div className='visible-xs m-t-xs'>{a.SaleLocation}</div>
					</Col>
					<Col width={colWidths.location} className='hidden-xs'>
						{a.SaleLocation}
					</Col>
				</Cols>
				<Cols className='m-t-n-xs'>
					<Col width={colWidths.status} className='hidden-xs' />
					<Col width={colWidths.time} />
					<Col width={colWidths.guests} />
					<Col width='*' className='text-italic'>{a.Notes}</Col>
				</Cols>
			</li>;
		});

		return <Flex.Horizontal>
			<Flex.Item className='p-sm hidden-xs'>
				<ReactDatePicker id='date-calendar'
					dateFormat={'dd/MM/yyyy'}
					dateFormatCalendar={'MMMM yyyy'}
					selected={this.state.currentDate}
					onChange={onCurrentDateChanged}
					locale={DateUtils.inferLocale()}
					todayButton={Res.Web.SHOW_APPOINTMENTS_TODAY_BTN()}
					dayClassName={date => this.state.monthlyAppointments.BusyDays.filter(d => DateUtils.areSameDate(date, d)).length > 0 ? 'text-bolder text-underline' : ''}
					inline />

				<div className='checkbox abc-checkbox m-l-xxs m-t-sm'>
					<input id='filterByPendingChk' type='checkbox' checked={this.state.filterByPendingStatus} onChange={(ev) => this.onFilterByPendingStatusChanged(ev.target.checked)} />
					<label htmlFor='filterByPendingChk'>{Res.Web.SHOW_APPOINTMENTS_PENDING_APPOINTMENTS_CHK()}</label>
				</div>

				<Button buttonStyle='info' className='btn-block m-t-xl m-r-sm' onClick={this.createNewAppointment}>
					<i className='fa fa-plus'></i> {Res.Web.SHOW_APPOINTMENTS_NEW_APPOINTMENT_BTN()}
				</Button>

			</Flex.Item>
			<Flex.Item size='*'>
				<Flex.Vertical>
					<Flex.Item>
						<Cols style={{ alignItems: 'center', textAlign: 'center' }} className='m-t-sm'>
							<Col width='60px' onClick={() => this.changeCurrentDate(DateUtils.addDays(this.state.currentDate, -1))}>
								<Button className='btn-block'><i className='fa fa-chevron-left fa-2x text-info' /></Button>
							</Col>
							<Col width='*' className='text-32'>
								{UI.Format.asDate(this.state.currentDate)}
							</Col>
							<Col width='60px' onClick={() => this.changeCurrentDate(DateUtils.addDays(this.state.currentDate, 1))}>
								<Button className='btn-block'><i className='fa fa-chevron-right fa-2x text-info' /></Button>
							</Col>
						</Cols>
					</Flex.Item>
					<Flex.Item>
						<Cols className='list-header'>
							<Col width={colWidths.status} className='hidden-xs' />
							<Col width={colWidths.time}>
								{Res.Web.SHOW_APPOINTMENTS_DATE_CLM()}
							</Col>
							<Col width={colWidths.guests} />
							<Col width={colWidths.customer}>
								{Res.Web.SHOW_APPOINTMENTS_CUSTOMER_CLM()}
							</Col>
							<Col width={colWidths.location} className='hidden-xs'>
								{Res.Web.SHOW_APPOINTMENTS_SALE_LOCATION_CLM()}
							</Col>
						</Cols>
					</Flex.Item>
					<Flex.Item size='*'>
						<ul className='list text-18'>{appointments}</ul>
					</Flex.Item>
				</Flex.Vertical>
			</Flex.Item>
		</Flex.Horizontal>;
	}
}

declare global {
	interface CommandType {
		showAppointments: () => Promise<Result<SaleModuleNextAction>>
	}
}

Command.register('showAppointments', Preconditions.and(Preconditions.ensure(Permission.Appointment_ShowList), Preconditions.ensureIsNotLite), async (): Promise<Result<SaleModuleNextAction>> => {
	const currentDate = lastCurrentDate;
	const monthlyAppointments = await AppointmentFacade.getMonthlyAppointments(currentDate, getFromDate(currentDate), getToDate(currentDate));
	return await Modal.show(ShowAppointmentsScreen, { currentDate: currentDate, monthlyAppointments: monthlyAppointments, filterByPendingStatus: lastFilterByPendingStatus });
});
